import { toaster } from '@/components/ui/toaster';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type { ApiResponse, OrganizationBillingContext } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';

/** The return URL is only a hint; Shopify's canonical observation decides access. */
export function useReconcileShopifyFreeApproval() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingActionScope();
  return useMutation<
    OrganizationBillingContext,
    AxiosError<ApiResponse> | Error,
    string,
    typeof scope
  >({
    mutationKey: ['shopifyFreeApproval', scope.userId, scope.organizationId, scope.brandId],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (operationId) => {
      assertCurrent(scope);
      if (!scope.userId || !scope.brandId || !scope.organizationId || !operationId.trim())
        throw new Error('No Shopify approval target');
      const response = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/shopify-free-approval/reconcile',
        { operationId }
      );
      assertCurrent(scope);
      const context = readBillingContext(response.data.data);
      if (
        !response.data.success ||
        !context ||
        context.channel !== 'shopify' ||
        context.brandId !== scope.brandId ||
        context.organizationId !== scope.organizationId ||
        context.shopifyFreeApproval?.operationId !== operationId
      )
        throw new Error('Invalid Shopify approval response');
      return context;
    },
    onSuccess: async (context, _operationId, target) => {
      if (!target) throw new Error('Missing Shopify approval target');
      assertCurrent(target);
      client.setQueryData(billingContextQueryKey(target), context);
      await client.invalidateQueries({ queryKey: ['subscription', target.brandId] });
      assertCurrent(target);
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError) return;
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'shopifyFreeApproval.readError'),
      });
    },
  });
}
